import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import closeFill from "@iconify-icons/eva/close-fill";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { MIconButton } from "src/theme";

import IsRole from "src/utils/IsRole";
import Roles from "src/utils/Roles";
import axios from "src/utils/axios";
import { addProductoSimpleSuccess } from "src/redux/slices/product";
import SimpleAddCategoryModal from "../../../categories/SimpleAdd/simpleAddCategoryModal";

const SimpleAddProduct = ({ onClose, onSelect }) => {
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { lastCategorySelectedWhenCreate, complementosValues } = useSelector(
    (state) => state.product
  );
  const { categories } = useSelector((state) => state.categories);
  const { claims } = useSelector((state) => state.authJwt);

  const isFranchisee = IsRole(claims.role, Roles.USER_FRANQUICICA);

  const createProduct = async (producto) => {
    const res = await axios.post("productos", JSON.stringify(producto), {
      headers: { "Content-Type": "application/json" },
    });
    const product = res.data.product;
    const complemento = {
      i: complementosValues.length,
      idProduct: product.id,
      idCategory: product.idCategory,
      name: product.nombre,
      price: 0.0,
      checked: false,
      order: null,
      isMandatory: true,
      maxQuantity: 1,
    };
    dispatch(addProductoSimpleSuccess(product));
    onSelect(complemento);
    onClose();
  };

  const formik = useFormik({
    initialValues: {
      nombre: "",
      description: "",
      precio: 0,
      taxes: 10,
      priceByMarket: false,
      isAvailable: true,
      isComplemento: true,
      category: lastCategorySelectedWhenCreate || categories[0]?.id,
    },
    validationSchema: Yup.object().shape({
      nombre: Yup.string().required("Nombre requerido"),
      precio: Yup.number(),
      category: Yup.number().required("Categoría requerida"),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        await createProduct({
          nombre: values.nombre,
          descripcion: values.description,
          precio: values.precio,
          cost: 0,
          margen: 0,
          margenPercent: 0,
          iva: values.taxes,
          isAvailable: values.isAvailable,
          priceByMarket: values.priceByMarket,
          haveNotes: false,
          unidades: null,
          images: [],
          idRestaurante: window.localStorage.getItem("userId"),
          idCategory: values.category,
          subCategory: null,
          soloAdmin: false,
          isComplemento: values.isComplemento,
          complementos: [],
          alergenos: [],
          controlStock: false,
          complementOrders: [],
        });
        enqueueSnackbar("Producto creado", {
          variant: "success",
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        setErrors(error);
        enqueueSnackbar("Ha ocurrido algún error", {
          variant: "warning",
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
      }
    },
  });

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        {categoryModalOpen && (
          <SimpleAddCategoryModal
            open={categoryModalOpen}
            handleClose={() => setCategoryModalOpen(false)}
            onSelect={(id) => setFieldValue("category", id)}
          />
        )}

        <Stack spacing={2.5}>
          {/* Nombre */}
          <TextField
            fullWidth
            label="Nombre del producto"
            {...getFieldProps("nombre")}
            error={Boolean(touched.nombre && errors.nombre)}
            helperText={touched.nombre && errors.nombre}
            disabled={isFranchisee}
            autoFocus
          />

          {/* Descripción */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Descripción (opcional)"
            {...getFieldProps("description")}
            disabled={isFranchisee}
          />

          {/* Categoría */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <FormControl fullWidth error={Boolean(touched.category && errors.category)}>
              <InputLabel>Categoría</InputLabel>
              <Select
                label="Categoría"
                native
                {...getFieldProps("category")}
                onChange={(e) => setFieldValue("category", Number(e.target.value))}
                value={values.category}
                disabled={isFranchisee}
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
              {touched.category && errors.category && (
                <FormHelperText>{errors.category}</FormHelperText>
              )}
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCategoryModalOpen(true)}
              sx={{ whiteSpace: "nowrap", mt: 1 }}
            >
              + Nueva
            </Button>
          </Box>

          {/* Precio */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label="Precio (IVA inc.)"
              {...getFieldProps("precio")}
              disabled={isFranchisee || values.priceByMarket}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                type: "number",
                inputProps: { min: 0, step: 0.01 },
              }}
            />
            <TextField
              select
              label="IVA %"
              {...getFieldProps("taxes")}
              onChange={(e) => setFieldValue("taxes", Number(e.target.value))}
              SelectProps={{ native: true }}
              sx={{ minWidth: 90 }}
              disabled={isFranchisee}
            >
              {[21, 10, 4, 0].map((v) => (
                <option key={v} value={v}>{v}%</option>
              ))}
            </TextField>
          </Box>

          {/* Switches */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "grey.100",
            }}
          >
            <FormControlLabel
              control={<Switch size="small" checked={values.isAvailable} onChange={(e) => setFieldValue("isAvailable", e.target.checked)} />}
              label={<Typography variant="body2">Disponible</Typography>}
              disabled={isFranchisee}
            />
            <FormControlLabel
              control={<Switch size="small" checked={values.isComplemento} onChange={(e) => setFieldValue("isComplemento", e.target.checked)} />}
              label={<Typography variant="body2">Solo complemento</Typography>}
              disabled={isFranchisee}
            />
            <FormControlLabel
              control={<Switch size="small" checked={values.priceByMarket} onChange={(e) => setFieldValue("priceByMarket", e.target.checked)} />}
              label={<Typography variant="body2">Precio s/mercado</Typography>}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, pt: 0.5 }}>
            <Button variant="outlined" color="inherit" fullWidth onClick={onClose}>
              Cancelar
            </Button>
            <LoadingButton type="submit" variant="contained" fullWidth loading={isSubmitting}>
              Crear producto
            </LoadingButton>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default SimpleAddProduct;
